<template>
  <div>
    <KTCard ref="preview" v-bind:title="'Edit External Bill of Lading'">
      <template v-slot:title>
        <h3 class="card-label">
          <router-link :to="{ name: 'list-external-bill-of-lading' }">{{
            $t("MENU.ITEM.ACTIVITIES.EXTERNAL_BILL_OF_LADING")
          }}</router-link>
          <i class="mr-2"></i>
          <small class="">{{ $t("LABELS.EDIT") }}</small>
        </h3>
      </template>
      <template v-slot:toolbar>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            class="mr-2"
            :defaultAction="'continue'"
            :isLoading="isLoadingExternalBillOfLading"
            with-copy
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
          <button
            class="btn btn-danger font-weight-bolder"
            @click="handleDelete"
          >
            {{ $t("BUTTON.DELETE") }}
          </button>
        </div>
      </template>
      <template v-slot:body>
        <BillOfLadingForm
          v-if="!loading"
          ref="BillOfLadingForm"
          :form="form"
          :staffList="staffList"
        ></BillOfLadingForm>
      </template>
      <template v-slot:foot>
        <div
          v-show="_.size(errors) > 0"
          role="alert"
          class="alert alert-danger"
        >
          <div class="alert-text" v-for="(error, i) in errors" :key="i">
            {{ error }}
          </div>
        </div>
      </template>
      <template v-slot:toolbarfoot>
        <div class="example-tools justify-content-center">
          <BackButton></BackButton>
          <SaveButtonDropdown
            class="mr-2"
            :defaultAction="'continue'"
            :isLoading="isLoadingExternalBillOfLading"
            with-copy
            v-on:save="onSave($event)"
          ></SaveButtonDropdown>
          <button
            class="btn btn-danger font-weight-bolder mr-2"
            @click="handleDelete"
          >
            {{ $t("BUTTON.DELETE") }}
          </button>
        </div>
      </template>
    </KTCard>
  </div>
</template>

<script>
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  FETCH_EXTERNAL_BILL_OF_LADING,
  DELETE_EXTERNAL_BILL_OF_LADING
} from "@/modules/bill-of-lading/store/external-bill-of-lading.module";
import { SAVE_BILL_OF_LADING } from "@/modules/bill-of-lading/store/bill-of-lading.module";
import { FETCH_SERVICES } from "@/modules/services/store/service.module";
import { FETCH_ASSIGNOR_LOCATIONS } from "@/modules/assignorslocation/store/location.module";
import { FETCH_MACHINES } from "@/modules/machines/store/machine.module";
import { FETCH_STAFF } from "@/modules/staff/store/staff.module";

import SaveButtonDropdown from "@/view/content/forms/components/SaveButtonDropdown.vue";
import KTCard from "@/view/content/Card.vue";
import BillOfLadingForm from "./BillOfLadingForm.vue";
import billOfLadingMixin from "./mixins/billOfLading.mixin";

import { mapGetters, mapState } from "vuex";

export default {
  name: "ExternalBillOfLadingEdit",
  mixins: [billOfLadingMixin],
  components: {
    KTCard,
    SaveButtonDropdown,
    BillOfLadingForm
  },
  data() {
    return {
      loading: true,
      errors: {},
      staffList: [],
      externalBillId: null,
      form: {
        id: null,
        billNumber: null,
        bolDate: null,
        assignorId: null,
        locationId: null,
        machine: null,
        staffId: null,
        note: null,
        work: [
          {
            id: null,
            serviceId: null,
            measureId: null,
            serviceQty: null,
            dateWorked: null,
            note: null,
            isUnloading: false,
            subcontractorsLocationId: null
          }
        ]
      }
    };
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: this.$i18n.t("MENU.ITEM.ACTIVITIES.EXTERNAL_BILL_OF_LADING"),
        route: { name: "list-external-bill-of-lading" }
      },
      { title: this.$i18n.t("LABELS.EDIT") }
    ]);

    this.fetchExternalBillOfLading();
  },
  computed: {
    ...mapGetters(["isLoadingExternalBillOfLading", "isLoadingBillOfLading"]),
    ...mapState({
      storeErrors: state => state.auth.errors
    })
  },
  methods: {
    async fetchExternalBillOfLading() {
      try {
        const data = await this.$store.dispatch(
          FETCH_EXTERNAL_BILL_OF_LADING,
          this.$route.params.id
        );
        const externalBill = data.data;
        this.externalBillId = externalBill.id;

        this.form = {
          id: null,
          billNumber: externalBill.billNumber,
          bolDate: externalBill.date,
          assignorId: null,
          locationId: null,
          machine: null,
          staffId: null,
          note: externalBill.note || null,
          work: [
            {
              id: null,
              serviceId: null,
              measureId: null,
              serviceQty: externalBill.workDone || null,
              dateWorked: externalBill.date,
              note: externalBill.note || null,
              isUnloading: false,
              subcontractorsLocationId: null
            }
          ]
        };

        const [
          machinesResponse,
          locationsResponse,
          servicesResponse,
          staffResponse
        ] = await Promise.all([
          this.$store.dispatch(FETCH_MACHINES),
          this.$store.dispatch(FETCH_ASSIGNOR_LOCATIONS),
          this.$store.dispatch(FETCH_SERVICES),
          this.$store.dispatch(FETCH_STAFF)
        ]);

        this.mapLocationData(externalBill, locationsResponse);
        this.mapServiceData(externalBill, servicesResponse);
        this.staffList = staffResponse.data.items || [];
        this.loading = false;

        await this.$nextTick();

        if (externalBill.machine?.id) {
          await this.selectMachineAndStaff(
            externalBill.machine.id,
            machinesResponse.data.items
          );
        }
      } catch (error) {
        this.handleFetchError(error);
      }
    },

    mapLocationData(externalBill, locationsResponse) {
      if (!externalBill.location) return;

      const location = locationsResponse.data.items?.find(
        l => l.code?.toLowerCase() === externalBill.location.toLowerCase()
      );

      if (location) {
        this.form.locationId = location.id;
        if (location.assignorId) {
          this.form.assignorId = location.assignorId;
        }
      } else {
        console.warn(`Location with code "${externalBill.location}" not found`);
      }
    },

    mapServiceData(externalBill, servicesResponse) {
      if (!externalBill.serviceName || !servicesResponse.data.items) return;

      const service = servicesResponse.data.items.find(
        s =>
          s.name?.toLowerCase().trim() ===
          externalBill.serviceName.toLowerCase().trim()
      );

      if (service) {
        this.form.work[0].serviceId = service.id;
        this.form.work[0].measureId = service.measureId;
        this.form.work[0].isUnloading = service.isUnloading || false;
      } else {
        console.warn(
          `Service with name "${externalBill.serviceName}" not found`
        );
      }
    },

    async selectMachineAndStaff(machineId, machines) {
      try {
        const machine = machines?.find(m => m.id === machineId);
        if (!machine) {
          console.warn(`Machine with ID ${machineId} not found`);
          return;
        }

        this.form.machine = {
          ...machine,
          machinable_id: machine.machinable_id || machine.id,
          machinable_type: "OwnMachineExpense"
        };

        await this.$nextTick();

        if (this.$refs.BillOfLadingForm) {
          this.$refs.BillOfLadingForm.localStaffList = this.staffList.map(
            staff => ({
              id: staff.id,
              name: staff.name
            })
          );
          this.$refs.BillOfLadingForm.staffListKey += 1;
        }

        await new Promise(resolve => setTimeout(resolve, 100));

        if (machine.lastUsedStaffId) {
          const staff = this.staffList.find(
            s => s.id === machine.lastUsedStaffId
          );
          if (staff) {
            this.form.staffId = machine.lastUsedStaffId;
            if (this.$refs.BillOfLadingForm) {
              this.$refs.BillOfLadingForm.form.staffId =
                machine.lastUsedStaffId;
            }
          }
        }
      } catch (error) {
        console.error("Error selecting machine and staff:", error);
      }
    },

    handleFetchError(error) {
      console.error("Error fetching external bill of lading:", error);
      this.$notify({
        group: "notify",
        type: "error",
        title: this.$t("ALERTS.ERROR"),
        text: "Failed to load external bill of lading"
      });
      this.$router.push({ name: "list-external-bill-of-lading" });
    },

    onSave(next) {
      let vm = this;
      this.$refs.BillOfLadingForm.$v.form.$touch();

      if (this.$refs.BillOfLadingForm.$v.form.$anyError) {
        vm.$notify({
          group: "notify",
          type: "error",
          title: "<i class='flaticon-close'></i> Warning",
          text: "Please fill all required fields correctly"
        });
        return;
      }

      if (this.form.machine) {
        if (!this.form.machine.machinable_id) {
          this.form.machine.machinable_id = this.form.machine.id;
        }
        this.form.machine.machinable_type = "OwnMachineExpense";
      }

      const gpsCheck = this.checkGpsData
        ? this.checkGpsData(this.form.machine, this.form.work)
        : Promise.resolve();

      gpsCheck
        .then(() => {
          this.$store
            .dispatch(SAVE_BILL_OF_LADING, this.form)
            .then(async data => {
              vm.errors = data.data.errors;
              let itemEdit = data.data;

              try {
                await this.$store.dispatch(
                  DELETE_EXTERNAL_BILL_OF_LADING,
                  this.externalBillId
                );

                vm.$notify({
                  group: "notify",
                  type: "success",
                  title: "<i class='flaticon2-checkmark'></i> Success",
                  text:
                    "Bill of lading created and external record removed successfully"
                });

                this.handleSaveNavigation(next, itemEdit);
              } catch (deleteError) {
                console.error(
                  "Failed to delete external bill of lading:",
                  deleteError
                );
                vm.$notify({
                  group: "notify",
                  type: "warning",
                  title: "<i class='flaticon-warning'></i> Warning",
                  text:
                    "Bill of lading created but failed to remove external record"
                });

                this.handleSaveNavigation(next, itemEdit);
              }
            })
            .catch(response => {
              this.errors = this.$errors.format(response.data.errors);

              vm.$notify({
                group: "notify",
                type: "error",
                title: this.$t("ALERTS.ERROR"),
                text: response.data.message
              });
            });
        })
        .catch(error => {
          console.error("GPS data check failed:", error);
          vm.$notify({
            group: "notify",
            type: "error",
            title: "<i class='flaticon-close'></i> Warning",
            text: "GPS data is missing or invalid"
          });
        });
    },

    handleSaveNavigation(next, itemEdit) {
      const routes = {
        continue: {
          name: "edit-bill-of-lading",
          params: { id: itemEdit.id }
        },
        copy: {
          name: "add-bill-of-lading",
          params: {
            externalBillData: {
              billNumber: null,
              bolDate: this.form.bolDate,
              machine: this.form.machine,
              work: this.form.work,
              note: this.form.note
            }
          }
        },
        new: { name: "add-bill-of-lading" },
        exit: { name: "list-bill-of-lading" }
      };

      if (routes[next]) {
        this.$router.push(routes[next]);
      }
    },

    handleDelete() {
      this.$dialog.confirm(this.$t("BUTTON.PLEASE_CONFIRM")).then(() => {
        this.$store
          .dispatch(DELETE_EXTERNAL_BILL_OF_LADING, this.$route.params.id)
          .then(data => {
            this.$notify({
              group: "notify",
              type: "success",
              title: "<i class='flaticon2-checkmark'></i> Success",
              text:
                data.message || "External bill of lading deleted successfully"
            });
            this.$router.push({ name: "list-external-bill-of-lading" });
          })
          .catch(error => {
            this.$notify({
              group: "notify",
              type: "error",
              title: this.$t("ALERTS.ERROR"),
              text:
                error.response?.data?.message ||
                "Failed to delete external bill of lading"
            });
          });
      });
    }
  }
};
</script>
